// src/pages/CampaignFlow/phases/Phase3Plans.jsx
import React, { useState, useEffect } from 'react';
import {
  XMarkIcon,
  CheckCircleIcon,
  CreditCardIcon,
  QrCodeIcon,
  RocketLaunchIcon,
  StarIcon,
  MegaphoneIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import apiClient from '../../../lib/apiClient';

function PIXModal({ pixCode, plan, onConfirm, onClose }) {
  const [polling, setPolling] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  // Simula webhook de confirmação
  const handleConfirm = async () => {
    setPolling(true);
    try {
      const r = await apiClient.request('/payment/webhook', {
        method: 'POST',
        body: JSON.stringify({
          campaign_id: onConfirm.campaignId,
          event: 'payment.confirmed',
        }),
      });
      const d = await r.json();
      if (d.success) {
        setConfirmed(true);
        setTimeout(() => onConfirm.callback(), 1500);
      }
    } catch {
      alert('Erro ao confirmar pagamento');
    } finally {
      setPolling(false);
    }
  };

  return (
    <div className="cf-modal-overlay">
      <div className="cf-modal cf-glass-card">
        <button className="cf-modal-close" onClick={onClose}><XMarkIcon style={{ width: 20, height: 20 }} /></button>

        {confirmed ? (
          <div className="cf-modal-success">
            <div className="cf-modal-success__icon"><CheckCircleIcon style={{ width: 48, height: 48, color: '#4ade80' }} /></div>
            <h3>Pagamento confirmado!</h3>
            <p>Ativando sua campanha...</p>
          </div>
        ) : (
          <>
            <h3 className="cf-modal-title"><CreditCardIcon style={{ width: 20, height: 20, display: 'inline', marginRight: 6 }} />Pagar com PIX</h3>
            <p className="cf-modal-sub">Plano <strong>{plan?.name}</strong> — R$ {plan?.price?.toFixed(2)}</p>

            <div className="cf-pix-block">
              <div className="cf-pix-qr">
                <div className="cf-pix-qr__placeholder">
                  <QrCodeIcon style={{ width: 48, height: 48, opacity: 0.5 }} />
                  <small>QR Code PIX</small>
                </div>
              </div>
              <div className="cf-pix-code">
                <label className="cf-label">Código PIX Copia e Cola</label>
                <div className="cf-pix-code__box">
                  <code>{pixCode}</code>
                  <button className="cf-btn cf-btn--outline cf-btn--xs"
                    onClick={() => { navigator.clipboard?.writeText(pixCode); }}>
                    Copiar
                  </button>
                </div>
              </div>
            </div>

            <button
              className="cf-btn cf-btn--primary cf-btn--full"
              onClick={handleConfirm}
              disabled={polling}
            >
              {polling ? 'Confirmando...' : <><CheckCircleIcon style={{ width: 16, height: 16, display: 'inline', marginRight: 4 }} />Confirmar Pagamento</>}
            </button>
            <p className="cf-modal-note">Em produção, o webhook é disparado automaticamente pela operadora PIX</p>
          </>
        )}
      </div>
    </div>
  );
}

export default function Phase3Plans({ campaignId, onNext }) {
  const [plans, setPlans]           = useState([]);
  const [selected, setSelected]     = useState(null);
  const [loading, setLoading]       = useState(true);
  const [checkoutData, setCheckout] = useState(null);
  const [showPix, setShowPix]       = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const r = await apiClient.request('/plans', { method: 'GET' });
        const d = await r.json();
        if (d.success && d.plans?.length) {
          setPlans(d.plans);
          // Seleciona plano "Pro Recommended" ou o do meio por padrão
          const rec = d.plans.find(p => p.recommended) || d.plans[1] || d.plans[0];
          setSelected(rec?.id || null);
        }
      } catch {}
      finally { setLoading(false); }
    };
    load();
  }, []);

  const handleCheckout = async () => {
    if (!selected) return;
    setLoading(true);
    try {
      const r = await apiClient.request(`/campaign/${campaignId}/checkout`, {
        method: 'POST',
        body: JSON.stringify({ plan_id: selected }),
      });
      const d = await r.json();
      if (d.success) {
        setCheckout(d);
        setShowPix(true);
      }
    } catch {}
    finally { setLoading(false); }
  };

  const selectedPlan = plans.find(p => p.id === selected);

  return (
    <div className="cf-phase cf-phase--plans">
      <div className="cf-mascot-row">
        <RocketLaunchIcon className="cf-mascot" style={{ width: 32, height: 32 }} />
        <div className="cf-mascot-speech">Turbine sua campanha e alcance mais pessoas!</div>
      </div>

      {loading && plans.length === 0 ? (
        <div className="cf-loading-sm">Carregando planos...</div>
      ) : (
        <div className="cf-plans-grid">
          {plans.map(plan => (
            <div
              key={plan.id}
              className={`cf-plan-card cf-glass-card ${selected === plan.id ? 'cf-plan-card--selected' : ''} ${plan.recommended ? 'cf-plan-card--rec' : ''}`}
              onClick={() => setSelected(plan.id)}
            >
              {plan.recommended && <div className="cf-plan-badge"><StarIcon style={{ width: 13, height: 13, display: 'inline', marginRight: 3 }} />Recomendado</div>}
              <div className="cf-plan-icon">{plan.unlimited ? <ArrowPathIcon style={{ width: 24, height: 24 }} /> : <MegaphoneIcon style={{ width: 24, height: 24 }} />}</div>
              <h3 className="cf-plan-name">{plan.name}</h3>
              <div className="cf-plan-price">
                <span className="cf-plan-price__currency">R$</span>
                <span className="cf-plan-price__val">{plan.price}</span>
              </div>
              {plan.unlimited
                ? <p className="cf-plan-feature">Impressões ilimitadas</p>
                : <p className="cf-plan-feature">{(plan.impressions || 0).toLocaleString('pt-BR')} impressões</p>
              }
              <div className={`cf-plan-radio ${selected === plan.id ? 'cf-plan-radio--on' : ''}`} />
            </div>
          ))}
        </div>
      )}

      <button
        className="cf-btn cf-btn--cta cf-btn--full"
        onClick={handleCheckout}
        disabled={!selected || loading}
      >
        {loading ? 'Aguarde...' : <><CreditCardIcon style={{ width: 16, height: 16, display: 'inline', marginRight: 6 }} />Pagar com PIX e Lançar!</>}
      </button>

      {showPix && checkoutData && (
        <PIXModal
          pixCode={checkoutData.pix_code}
          plan={checkoutData.plan}
          onClose={() => setShowPix(false)}
          onConfirm={{
            campaignId,
            callback: () => { setShowPix(false); onNext({ planId: selected, plan: selectedPlan }); },
          }}
        />
      )}
    </div>
  );
}
